import { useState, useEffect, useCallback } from 'react';
import { Shield, Loader2, Search, ToggleLeft, ToggleRight, Plus, Trash2, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { adminAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

export function ExemptionsTab() {
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [reason, setReason] = useState('');

  // Entity search state
  const [entitySearch, setEntitySearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const fetchExemptions = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listExemptions();
      setExemptions(res.data.exemptions || []);
    } catch { setExemptions([]); }
    setLoading(false);
  };

  useEffect(() => { fetchExemptions(); }, []);

  // Debounced entity search
  useEffect(() => {
    if (!entitySearch.trim() || entitySearch.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminAPI.listUsers({ search: entitySearch.trim(), limit: 8 });
        setSearchResults(res.data.users || []);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [entitySearch]);

  const handleToggle = async (ex) => {
    try {
      await adminAPI.upsertExemption({ entity_user_id: ex.entity_user_id, is_exempt: !ex.is_exempt, reason: ex.reason });
      toast.success(ex.is_exempt ? 'Exemption removed' : 'Entity exempted');
      fetchExemptions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAdd = async () => {
    if (!selectedEntity) { toast.error('Please select an entity'); return; }
    try {
      await adminAPI.upsertExemption({ entity_user_id: selectedEntity.id, is_exempt: true, reason });
      toast.success('Exemption added');
      setAddOpen(false);
      setEntitySearch('');
      setSearchResults([]);
      setSelectedEntity(null);
      setReason('');
      fetchExemptions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add exemption'); }
  };

  const handleDelete = async (entityUid) => {
    try {
      await adminAPI.deleteExemption(entityUid);
      toast.success('Exemption deleted');
      fetchExemptions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openAddDialog = () => {
    setAddOpen(true);
    setEntitySearch('');
    setSearchResults([]);
    setSelectedEntity(null);
    setReason('');
  };

  return (
    <div className="space-y-4" data-testid="admin-exemptions">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">CyberCheck LLC Remediation Exemptions</h3>
          <p className="text-xs text-muted-foreground">Exempt entities are hidden from Regulatory users but visible to the entity and Super Admins.</p>
        </div>
        <Button onClick={openAddDialog} data-testid="add-exemption-button">
          <Plus className="mr-2 h-4 w-4" />Add Exemption
        </Button>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : exemptions.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">No exemptions configured</p>
          ) : (
            <div className="space-y-2">
              {exemptions.map(ex => (
                <div key={ex.id || ex.entity_user_id} className="flex items-center justify-between rounded-lg border border-border/60 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Shield className={cn('h-4 w-4', ex.is_exempt ? 'text-vrs-amber' : 'text-muted-foreground')} />
                    <div>
                      <p className="text-sm font-medium">{ex.entity_name || ex.entity_email || ex.entity_user_id}</p>
                      {ex.entity_url && <p className="text-[10px] text-muted-foreground">{ex.entity_url}</p>}
                      {ex.reason && <p className="text-xs text-muted-foreground mt-0.5">Reason: {ex.reason}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn('text-[10px]', ex.is_exempt ? 'border-vrs-amber/30 text-vrs-amber' : 'border-vrs-green/30 text-vrs-green')}>
                      {ex.is_exempt ? 'Exempt' : 'Included'}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleToggle(ex)} data-testid="toggle-exemption">
                      {ex.is_exempt ? <ToggleRight className="h-4 w-4 text-vrs-amber" /> : <ToggleLeft className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(ex.entity_user_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Exemption Dialog with Entity Search */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Exemption</DialogTitle>
            <DialogDescription>Search for an entity by name or email, then select it to exempt from Regulatory scan results.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Entity Search */}
            <div className="space-y-2">
              <Label>Search Entity</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={entitySearch}
                  onChange={e => { setEntitySearch(e.target.value); setSelectedEntity(null); }}
                  placeholder="Type a name or email..."
                  className="pl-9 bg-muted/30 border-border"
                  data-testid="exemption-entity-search"
                  autoFocus
                />
                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && !selectedEntity && (
                <div className="rounded-lg border border-border/60 max-h-[200px] overflow-y-auto">
                  {searchResults.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => { setSelectedEntity(u); setEntitySearch(''); setSearchResults([]); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/30 transition-[background-color] duration-200 border-b border-border/30 last:border-0"
                      data-testid="exemption-search-result"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{u.name || u.email}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{u.email}{u.entity_name ? ` \u2014 ${u.entity_name}` : ''}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] capitalize shrink-0">{u.role || 'unassigned'}</Badge>
                    </button>
                  ))}
                </div>
              )}

              {entitySearch.trim().length >= 2 && searchResults.length === 0 && !searching && !selectedEntity && (
                <p className="text-xs text-muted-foreground text-center py-2">No entities found</p>
              )}
            </div>

            {/* Selected Entity */}
            {selectedEntity && (
              <div className="rounded-lg border border-primary/40 bg-accent/20 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{selectedEntity.name || selectedEntity.email}</p>
                    <p className="text-xs text-muted-foreground">{selectedEntity.email}</p>
                    {selectedEntity.entity_name && <p className="text-xs text-muted-foreground">{selectedEntity.entity_name}{selectedEntity.entity_url ? ` \u2014 ${selectedEntity.entity_url}` : ''}</p>}
                  </div>
                  <Check className="h-4 w-4 text-primary shrink-0" />
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-1">
              <Label>Reason (optional)</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Hired CyberCheck LLC for remediation" className="bg-muted/30 border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAdd} disabled={!selectedEntity} data-testid="exemption-submit">
              Add Exemption
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
